import {
  PaginatedResponse,
  PaginationParams
} from '../types/pagination';
import { ChatUserStatus, User, UserRegisterRequest, UserUpdateRequest } from '../types/user';
import { ApiClient, ApiError } from './apiClient';

export interface UserSearchParams extends PaginationParams {
  role: string;
  search?: string;
  /** Filter by ChatUserStatus: 0 active, 1 warned, 2 banned */
  status?: ChatUserStatus | number;
}

export class UserService {
  constructor(private api: ApiClient) { }

  list(params: UserSearchParams) {
    const query: Record<string, string | number | undefined> = {
      role: params.role,
      search: params.search,
      status: params.status,
      page: params.page,
      limit: params.limit
    };

    return this.api
      .get<PaginatedResponse<User>>('/api/users/search', query)
      .then(result => {
        if (result.success && result.data) {
          return result.data;
        }
        return {
          data: [],
          meta: {
            page: Number(params.page ?? 1),
            limit: Number(params.limit ?? 10),
            total: 0,
            totalPages: 0
          }
        };
      });
  }

  getById(id: string) {
    return this.api
      .get<User>(`/api/users/${encodeURIComponent(id)}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  create(payload: UserRegisterRequest) {
    return this.api.post<User>('/api/users', payload).then(result => {
      if (!result.success || !result.data) {
        throw new ApiError(0, result.error ?? 'Failed to create user');
      }
      return result.data;
    });
  }

  update(id: string, payload: UserUpdateRequest) {
    return this.api
      .put<User>(`/api/users/${encodeURIComponent(id)}`, payload)
      .then(result => {
        if (!result.success || !result.data) {
          throw new ApiError(0, result.error ?? 'Failed to update user');
        }
        return result.data;
      });
  }

  delete(id: string) {
    return this.api
      .delete<void>(`/api/users/${encodeURIComponent(id)}`)
      .then(result => {
        if (!result.success) {
          throw new ApiError(0, result.error ?? 'Failed to delete user');
        }
      });
  }

  /** Get users by role (e.g. agent). Returns id (bigint-as-string) and name. */
  getByRole(role: string) {
    return this.api.get<{ id: string; name: string }[]>(
      `/api/users/role/${encodeURIComponent(role)}`
    ).then(result => (result.success && result.data ? result.data : []));
  }
}

